import { SupportedCountryCode } from './CountryCode';

/**
 * The BCP 47 locales served by the consumer web app as URL prefix (e.g. /de-DE/...).
 * Must stay in sync with the footer locale switcher (Locale enum) — the
 * Record maps below enforce exhaustiveness at compile time.
 */
export const APP_LOCALES = ['en-US', 'en-GB', 'de-DE', 'de-AT', 'de-CH'] as const;
export type AppLocale = (typeof APP_LOCALES)[number];

export type SupportedLanguageCode = 'de' | 'en';

export function isAppLocale(value: unknown): value is AppLocale {
    return typeof value === 'string' && (APP_LOCALES as readonly string[]).includes(value);
}

// Record<AppLocale, ...> forces exhaustiveness: adding a locale breaks compilation here.
const APP_LOCALE_TO_COUNTRY: Record<AppLocale, SupportedCountryCode> = {
    'de-DE': 'DE',
    'de-AT': 'AT',
    'de-CH': 'CH',
    'en-GB': 'GB',
    'en-US': 'US'
};

export function getCountryCodeFromAppLocale(locale: AppLocale): SupportedCountryCode {
    return APP_LOCALE_TO_COUNTRY[locale];
}

export function getLanguageCodeFromAppLocale(locale: AppLocale): SupportedLanguageCode {
    return locale.slice(0, 2) as SupportedLanguageCode;
}

/**
 * Single source of truth for "country assumed from a bare language code".
 * Only for locales that carry no country (e.g. 'de', 'en' in apps without
 * country-coded URLs) — prefer getCountryCodeFromAppLocale wherever the
 * country is known from the URL locale.
 */
export const DEFAULT_COUNTRY_CODE_BY_LANGUAGE: Record<SupportedLanguageCode, SupportedCountryCode> = {
    de: 'DE',
    en: 'US'
};
